import {
  combineLatest,
  from,
  iif,
  MonoTypeOperatorFunction,
  Observable,
  ObservableInput,
  ObservedValueOf,
  of,
  OperatorFunction,
  pipe,
  Subject,
  throwError,
  timer,
} from 'rxjs';
import { catchError, delayWhen, filter, map, retryWhen, scan, switchMap, takeUntil, tap } from 'rxjs/operators';

export const ifExists = <T>(property?: keyof T) =>
  pipe(filter((value: T) => (property === undefined ? !!value : !!value?.[property])));

export const onFirst = <T>(next: (x: T) => void, callCount = 0) =>
  pipe(
    switchMap((value: T) => iif(() => callCount === 0, of(value).pipe(tap({ next })), of(value))),
    tap(() => (callCount += 1)),
  );

export const onSubsequent = <T>(next: (x: T) => void, callCount = 0) =>
  pipe(
    switchMap((value: T) => iif(() => callCount > 0, of(value).pipe(tap({ next })), of(value))),
    tap(() => (callCount += 1)),
  );

export const createSource = <T>(source: Observable<T> | (() => Observable<T>)) =>
  typeof source === 'function' ? source() : source;

export const waitOn = <T>(project: (value: T) => ObservableInput<any>): MonoTypeOperatorFunction<T> =>
  switchMap((value: T) => from(project(value)).pipe(map(() => value)));

export const matchesCorrelationId = <
  T extends [{ correlationId: string }, { correlationId: string }],
>(): MonoTypeOperatorFunction<T> =>
  filter(([{ correlationId: expectedCorrelation }, { correlationId }]) => expectedCorrelation === correlationId);

// Uses filter to only allow those with correlationProcesses that match input
export const matchesCorrelationProcess = <T extends { criteria?: { correlationProcess: string } }>(
  matchingProcess: string,
) => filter(({ criteria }: T) => criteria?.correlationProcess === matchingProcess);

// Uses filter to only allow those with correlationProcesses that _do not_ match input
export const filterCorrelationProcess = <T extends { criteria?: { correlationProcess: string } }>(
  matchingProcess: string,
) => filter(({ criteria }: T) => criteria?.correlationProcess !== matchingProcess);

export const filterUntilPredicatePassed = <T>(
  predicate: (value: T, index: number) => boolean,
  predicatePassed = false,
): MonoTypeOperatorFunction<T> =>
  pipe(
    switchMap((value: T) =>
      iif(
        () => predicatePassed,
        of(value),
        of(value).pipe(
          filter(predicate),
          tap(() => (predicatePassed = true)),
        ),
      ),
    ),
  );

export const synchronizeOn = <T>(
  getLock: (value: T) => boolean,
  acquireLock: (value: T) => void,
  nextDelay: (delay: number | null) => number,
) =>
  ((delayFor: number = nextDelay(null)) =>
    pipe(
      map((value: T) => ({
        value,
        isPending: getLock(value),
      })),
      map(({ value, isPending }) => {
        if (isPending) {
          throw { waitPending: true, value };
        }
        return value;
      }),
      retryWhen(errors =>
        errors.pipe(
          map(err => {
            if (err.waitPending && delayFor) {
              return err.value;
            }
            throw err;
          }),
          delayWhen(() => timer(delayFor)),
          tap(() => (delayFor = nextDelay(delayFor))),
        ),
      ),
      catchError(err => of(err.value as T)),
      map((value: T) => (acquireLock(value), value)),
    ))();

const exists = (value: unknown): boolean => value != null;
const allExist = (...array: any[]) => array.every(exists);

// v v v v v v v v v v v v v v v v v v v v v v v v v v v v v v v v v v v v v v v v v v v v v v v v v v v v v v v v v v//
// Copyright (c) 2020-2021 BrieBug and Contributors (briebug, Jon Rista)
//
// Permission is hereby granted, free of charge, to any person obtaining a copy of this software and associated
// documentation files (the "Software"), to deal in the Software without restriction, including without limitation
// the rights to use, copy, modify, merge, publish, distribute, sublicense, and/or sell copies of the Software, and
// to permit persons to whom the Software is furnished to do so, subject to the following conditions:
//
// The above copyright notice and this permission notice shall be included in all copies or substantial portions of
// the Software.
//
// THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO
// THE WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
// AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER LIABILITY, WHETHER IN AN ACTION OF CONTRACT,
// TORT OR OTHERWISE, ARISING FROM, OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE SOFTWARE.

export function waitForAll<T, O2 extends ObservableInput<any>>(
  {
    retryDelay,
    maxRetries,
    predicate,
  }: {
    retryDelay?: number;
    maxRetries?: number;
    predicate?: (v2: ObservedValueOf<O2>) => boolean;
  },
  o2: O2,
): OperatorFunction<T, [T, ObservedValueOf<O2>]>;
export function waitForAll<T, O2 extends ObservableInput<any>, O3 extends ObservableInput<any>>(
  {
    retryDelay,
    maxRetries,
    predicate,
  }: {
    retryDelay?: number;
    maxRetries?: number;
    predicate?: (v2: ObservedValueOf<O2>, v3: ObservedValueOf<O3>) => boolean;
  },
  o2: O2,
  o3: O3,
): OperatorFunction<T, [T, ObservedValueOf<O2>, ObservedValueOf<O3>]>;
export function waitForAll<
  T,
  O2 extends ObservableInput<any>,
  O3 extends ObservableInput<any>,
  O4 extends ObservableInput<any>,
>(
  {
    retryDelay,
    maxRetries,
    predicate,
  }: {
    retryDelay?: number;
    maxRetries?: number;
    predicate?: (v2: ObservedValueOf<O2>, v3: ObservedValueOf<O3>, v4: ObservedValueOf<O4>) => boolean;
  },
  o2: O2,
  o3: O3,
  o4: O4,
): OperatorFunction<T, [T, ObservedValueOf<O2>, ObservedValueOf<O3>, ObservedValueOf<O4>]>;
export function waitForAll<
  T,
  O2 extends ObservableInput<any>,
  O3 extends ObservableInput<any>,
  O4 extends ObservableInput<any>,
  O5 extends ObservableInput<any>,
>(
  {
    retryDelay,
    maxRetries,
    predicate,
  }: {
    retryDelay?: number;
    maxRetries?: number;
    predicate?: (
      v2: ObservedValueOf<O2>,
      v3: ObservedValueOf<O3>,
      v4: ObservedValueOf<O4>,
      v5: ObservedValueOf<O5>,
    ) => boolean;
  },
  o2: O2,
  o3: O3,
  o4: O4,
  o5: O5,
): OperatorFunction<T, [T, ObservedValueOf<O2>, ObservedValueOf<O3>, ObservedValueOf<O4>, ObservedValueOf<O5>]>;
export function waitForAll<
  T,
  O2 extends ObservableInput<any>,
  O3 extends ObservableInput<any>,
  O4 extends ObservableInput<any>,
  O5 extends ObservableInput<any>,
  O6 extends ObservableInput<any>,
>(
  {
    retryDelay,
    maxRetries,
    predicate,
  }: {
    retryDelay?: number;
    maxRetries?: number;
    predicate?: (
      v2: ObservedValueOf<O2>,
      v3: ObservedValueOf<O3>,
      v4: ObservedValueOf<O4>,
      v5: ObservedValueOf<O5>,
      v6: ObservedValueOf<O6>,
    ) => boolean;
  },
  o2: O2,
  o3: O3,
  o4: O4,
  o5: O5,
  o6: O6,
): OperatorFunction<
  T,
  [T, ObservedValueOf<O2>, ObservedValueOf<O3>, ObservedValueOf<O4>, ObservedValueOf<O5>, ObservedValueOf<O6>]
>;
export function waitForAll<
  T,
  O2 extends ObservableInput<any>,
  O3 extends ObservableInput<any>,
  O4 extends ObservableInput<any>,
  O5 extends ObservableInput<any>,
  O6 extends ObservableInput<any>,
  O7 extends ObservableInput<any>,
>(
  {
    retryDelay,
    maxRetries,
    predicate,
  }: {
    retryDelay?: number;
    maxRetries?: number;
    predicate?: (
      v2: ObservedValueOf<O2>,
      v3: ObservedValueOf<O3>,
      v4: ObservedValueOf<O4>,
      v5: ObservedValueOf<O5>,
      v6: ObservedValueOf<O6>,
      v7: ObservedValueOf<O7>,
    ) => boolean;
  },
  o2: O2,
  o3: O3,
  o4: O4,
  o5: O5,
  o6: O6,
  o7: O7,
): OperatorFunction<
  T,
  [
    T,
    ObservedValueOf<O2>,
    ObservedValueOf<O3>,
    ObservedValueOf<O4>,
    ObservedValueOf<O5>,
    ObservedValueOf<O6>,
    ObservedValueOf<O7>,
  ]
>;
export function waitForAll<
  T,
  O2 extends ObservableInput<any>,
  O3 extends ObservableInput<any>,
  O4 extends ObservableInput<any>,
  O5 extends ObservableInput<any>,
  O6 extends ObservableInput<any>,
  O7 extends ObservableInput<any>,
  O8 extends ObservableInput<any>,
>(
  {
    retryDelay,
    maxRetries,
    predicate,
  }: {
    retryDelay?: number;
    maxRetries?: number;
    predicate?: (
      v2: ObservedValueOf<O2>,
      v3: ObservedValueOf<O3>,
      v4: ObservedValueOf<O4>,
      v5: ObservedValueOf<O5>,
      v6: ObservedValueOf<O6>,
      v7: ObservedValueOf<O7>,
      v8: ObservedValueOf<O8>,
    ) => boolean;
  },
  o2: O2,
  o3: O3,
  o4: O4,
  o5: O5,
  o6: O6,
  o7: O7,
  o8: O8,
): OperatorFunction<
  T,
  [
    T,
    ObservedValueOf<O2>,
    ObservedValueOf<O3>,
    ObservedValueOf<O4>,
    ObservedValueOf<O5>,
    ObservedValueOf<O6>,
    ObservedValueOf<O7>,
    ObservedValueOf<O8>,
  ]
>;

export function waitForAll<T, R>(
  {
    retryDelay,
    maxRetries,
    predicate,
  }: {
    retryDelay?: number;
    maxRetries?: number;
    predicate?: (...values: any[]) => boolean;
  },
  ...observables: ObservableInput<any>[]
): OperatorFunction<T, R> {
  return pipe(
    map((action: T) => ({
      action,
      done: new Subject(),
    })),
    switchMap(({ action, done }) =>
      combineLatest(observables.map(o => from(o).pipe(takeUntil(done)))).pipe(
        switchMap(others =>
          iif(
            () => (predicate || allExist)(...others),
            of(action).pipe(
              map((action: T): R => [action, ...others] as unknown as R),
              tap({
                next: () =>
                  setTimeout(() => {
                    done.next(undefined);
                    done.complete();
                  }, 1),
              }),
            ),
            throwError(action),
          ),
        ),
        retryWhen(actions =>
          actions.pipe(
            scan((acc, action) => ({ action, retryCount: acc.retryCount + 1 }), { retryCount: 0, action: undefined }),
            tap(({ retryCount }) => {
              if (retryCount >= (maxRetries ?? 5)) {
                setTimeout(() => {
                  done.next(undefined);
                  done.complete();
                }, 1);
                throw { message: 'waitForAll timed out', action, retryCount };
              }
            }),
            delayWhen(() => timer(retryDelay ?? 100)),
          ),
        ),
      ),
    ),
  );
}
